import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { TeamMember } from '../lib/about';
import { useLanguage } from '../contexts/LanguageContext';
import { TeamMemberCard } from './TeamMemberCard';

interface TeamCarouselProps {
  members: TeamMember[];
}

export function TeamCarousel({ members }: TeamCarouselProps) {
  const { language } = useLanguage(); 
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(4); 
  const [isSliding, setIsSliding] = useState(false);
  const [visibleSlides, setVisibleSlides] = useState(4);
  const [cardRotations, setCardRotations] = useState<Record<string, number>>({});
  const [visibleCards, setVisibleCards] = useState<Set<string>>(new Set());

  const touchStartXRef = useRef<number | null>(null);
  const touchEndXRef = useRef<number | null>(null);
  const MIN_SWIPE_DISTANCE = 50; // Minimum distance for a swipe to be registered

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = window.innerWidth;
        setContainerWidth(containerRef.current.offsetWidth);

        let newVisibleSlides = 4;
        if (width >= 1536) {
          newVisibleSlides = 4;
        } else if (width >= 1024) {
          newVisibleSlides = 3;
        } else if (width >= 768) {
          newVisibleSlides = 2;
        } else {
          newVisibleSlides = 1;
        }
        setVisibleSlides(newVisibleSlides);
        setCurrentSlide(newVisibleSlides); 
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [members.length]);

  useEffect(() => {
    setVisibleCards(new Set(members.map(member => member.id)));
  }, [members]);

  const handleCardClick = (cardId: string) => {
    // Prevent card click action if a swipe was just performed
    if (Math.abs((touchEndXRef.current ?? 0) - (touchStartXRef.current ?? 0)) > MIN_SWIPE_DISTANCE / 2) {
        // Reset touch refs to allow immediate next click if it wasn't a swipe
        touchStartXRef.current = null;
        touchEndXRef.current = null;
        return;
    }
    setCardRotations(prev => {
      const newRotations = { ...prev };
      
      Object.entries(newRotations).forEach(([id, rotation]) => {
        if (id !== cardId && rotation % 360 > 0) {
          newRotations[id] = rotation + 180;
        }
      });
      
      newRotations[cardId] = (newRotations[cardId] || 0) + 180;
      
      return newRotations;
    });
     // Reset touch refs after a click
    touchStartXRef.current = null;
    touchEndXRef.current = null;
  };

  const allSlides = members.length > 0 && visibleSlides > 0 ? [
    ...members.slice(-visibleSlides),
    ...members,
    ...members.slice(0, visibleSlides)
  ] : [];

  const handlePrevious = () => {
    if (isSliding || members.length === 0) return;
    setIsSliding(true);
    setCurrentSlide(prev => prev - 1);
  };

  const handleNext = () => {
    if (isSliding || members.length === 0) return;
    setIsSliding(true);
    setCurrentSlide(prev => prev + 1);
  };

  const handleTransitionEnd = () => {
    setIsSliding(false);

    let newSlideValueAfterJump = currentSlide;
    let needsToJump = false;

    if (members.length > 0 && currentSlide < visibleSlides) { 
      newSlideValueAfterJump = currentSlide + members.length;
      needsToJump = true;
    } 
    else if (members.length > 0 && currentSlide >= members.length + visibleSlides) {
      newSlideValueAfterJump = currentSlide - members.length;
      needsToJump = true;
    }

    const finalSlideIndex = needsToJump ? newSlideValueAfterJump : currentSlide;
    const currentlyVisibleSlidesInAllSlides = allSlides.length > 0 ? allSlides.slice(
      finalSlideIndex, 
      finalSlideIndex + visibleSlides 
    ) : [];
    
    const visibleCardIdsNow = new Set(
      currentlyVisibleSlidesInAllSlides.map(member => member.id)
    );

    setCardRotations(prevRotations => {
      const updatedRotations = { ...prevRotations };
      Object.keys(updatedRotations).forEach(id => {
        if ((updatedRotations[id] % 360 !== 0) && !visibleCardIdsNow.has(id)) {
          updatedRotations[id] = 0; 
        }
      });
      return updatedRotations;
    });
    
    if (needsToJump) {
      setCurrentSlide(newSlideValueAfterJump);
    }
  };

  const getCurrentIndex = () => {
    if (members.length === 0) return 0;
    if (currentSlide < visibleSlides) {
      return (members.length - visibleSlides + currentSlide) % members.length;
    }
    if (currentSlide >= members.length + visibleSlides) {
      return (currentSlide - (members.length + visibleSlides)) % members.length;
    }
    return (currentSlide - visibleSlides) % members.length;
  };
  
  const getSlideWidth = () => (containerWidth > 0 && visibleSlides > 0) ? containerWidth / visibleSlides : 0;

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isSliding) return;
    touchStartXRef.current = e.touches[0].clientX;
    touchEndXRef.current = e.touches[0].clientX; // Initialize endX
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isSliding || touchStartXRef.current === null) return;
    touchEndXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (isSliding || touchStartXRef.current === null || touchEndXRef.current === null) {
      return;
    }

    const distance = touchEndXRef.current - touchStartXRef.current;

    if (Math.abs(distance) > MIN_SWIPE_DISTANCE) { // Check if it's a significant swipe
      if (distance > 0) { // Swipe right
        handlePrevious();
      } else { // Swipe left
        handleNext();
      }
    } else {
      // If it's not a swipe, it might be a tap.
      // The handleCardClick logic will determine if it was a click on a card.
      // We don't want to interfere with that, but ensure touch refs are reset if it wasn't a click handled by handleCardClick.
      // This case is tricky because handleCardClick itself resets the refs.
      // If no card was clicked, and it wasn't a swipe, reset for sanity.
      // A small timeout ensures this runs after a potential click handler.
      setTimeout(() => {
        if (touchStartXRef.current !== null) { // If not already reset by handleCardClick
            touchStartXRef.current = null;
            touchEndXRef.current = null;
        }
      }, 0);
    }
    // For swipes, reset immediately. For taps, handleCardClick or the timeout above will manage it.
    if (Math.abs(distance) > MIN_SWIPE_DISTANCE) {
        touchStartXRef.current = null;
        touchEndXRef.current = null;
    }
  };


  if (members.length === 0) {
    return <div className="text-center p-8">No team members to display.</div>;
  }

  return (
    <div className="relative px-8 max-w-[1920px] mx-auto">
      <div 
        className="overflow-hidden" 
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div  
          className={`flex ${isSliding && members.length > 0 ? 'transition-transform duration-500 ease-in-out' : ''}`}
          style={{  
            transform: `translateX(-${currentSlide * getSlideWidth()}px)`,
            width: `${getSlideWidth() * allSlides.length}px`
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {allSlides.map((member, index) => (
            <div  
              key={`${member.id}-${index}`}
              style={{ width: `${getSlideWidth()}px` }}
              className="px-2 md:px-4 flex-shrink-0"
            >
              <TeamMemberCard
                member={member}
                rotationDegrees={cardRotations[member.id] || 0}
                onCardClick={handleCardClick}
                isVisible={visibleCards.has(member.id)} 
              />
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={handlePrevious}
        className="absolute left-0 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white shadow hover:bg-bamboo-50 transition-colors duration-300 disabled:opacity-50"
        disabled={isSliding || members.length === 0}
      >
        <ChevronLeft className="w-6 h-6 text-bamboo-600" />
      </button>

      <button
        onClick={handleNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white shadow hover:bg-bamboo-50 transition-colors duration-300 disabled:opacity-50"
        disabled={isSliding || members.length === 0}
      >
        <ChevronRight className="w-6 h-6 text-bamboo-600" />
      </button>

      <div className="flex justify-center gap-2 mt-8">
        {members.map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-colors duration-300 ${
              getCurrentIndex() === index ? 'bg-bamboo-600' : 'bg-bamboo-200 hover:bg-bamboo-300'
            }`}
            onClick={() => {
              if (!isSliding && members.length > 0) {
                setIsSliding(true);
                setCurrentSlide(index + visibleSlides);
                setCardRotations({}); 
              }
            }}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
